import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GenericAutocomplete } from "../../../../../../../common/GenericAutocomplete";
import { AssistantValue } from "../../../../../../../services/api/instances/assistantsApi/types";
import { AppLabel } from "../../../../../../../shared/components/AppLabel";
import { AppLoading } from "../../../../../../../shared/components/AppLoading";
import { TValueForService } from "../../../../../../../services/api/instances/storageApi/types/service_types";
import { getUpdatedChatbotValue } from "../../../../../helpers/getUpdatedChatbotValue";
import { NormalizedStorageObject } from "../../../../../../../services/api/instances/storageApi/types/object_types";
import { ChatbotValue } from "../../../../../../../services/api/instances/botApi/types";
import styles from "./styles.module.css";

interface Props {
  assistants: NormalizedStorageObject<AssistantValue>[];
  selectedObject: NormalizedStorageObject<ChatbotValue> | null;
  saveChatbotValue: (
    value: TValueForService<"ai_agents_telegram_bots">,
  ) => void;
}

export const ChatbotAssistantSelection = ({
  assistants,
  selectedObject,
  saveChatbotValue,
}: Props) => {
  const [selectedValue, setSelectedValue] =
    useState<NormalizedStorageObject<AssistantValue> | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!selectedObject?.value) return;
    const assistant = assistants?.find(
      (a) =>
        a?.value?.assistant_object?.id ===
        selectedObject?.value?.tg_bot_object?.asst_name,
    );
    setSelectedValue(assistant ?? null);
  }, [
    selectedObject?.id,
    selectedObject?.value?.tg_bot_object?.asst_name,
    assistants,
  ]);

  const onSelectChatbotAssistant = useCallback(
    (newValue: NormalizedStorageObject<AssistantValue> | null) => {
      setSelectedValue(newValue);
      if (!selectedObject?.value?.tg_bot_object) return;
      const updatedValue = getUpdatedChatbotValue(selectedObject?.value, {
        asst_name: newValue ? newValue?.value?.assistant_object?.id : null,
        asst_ref: newValue ? newValue?.ref : null,
      });
      saveChatbotValue(updatedValue);
    },
    [selectedValue, selectedObject],
  );

  return (
    <>
      <AppLabel className={styles.label}>
        {t("chatbot.default_assistant_label")}
      </AppLabel>
      {assistants ? (
        <GenericAutocomplete<NormalizedStorageObject<AssistantValue>>
          options={assistants?.filter((a) => a.value !== null)}
          value={selectedValue}
          onChange={onSelectChatbotAssistant}
          emptyOptionLabel={t("chatbot.without_assistant")}
          getOptionKey={(asst) => asst.ref}
          getLabel={(asst) => asst.friendly_name}
          getValue={(asst) => asst.id}
          autocompleteStyle={"select"}
          className={styles["autocomplete-margin"]}
        />
      ) : (
        <AppLoading small />
      )}
    </>
  );
};
